/**
 * Income-Statement Sankey topology model — fixed-column, forward-only flow,
 * no same-column links or U-turn beziers.
 *
 * Design:
 *  - ALL links span exactly one column (col(to) === col(from) + 1). No exceptions.
 *  - Nodes NEVER change columns based on sign. A node's `kind` changes but its
 *    column is invariant.
 *  - Loss nodes are token-sized: DUST inbound AND DUST outbound. The entire
 *    loss region renders at minimum height (labels show real values).
 *    When pretaxLoss && POOL > 0 && !operatingLoss, totalProfit uses real
 *    budget distribution (same as the profitable path) because distributable
 *    flow exists. The DUST collapse only fires when POOL ≤ 0 or operatingLoss
 *    (where inbound was already collapsed by routeNonOp).
 *  - Non-loss sources emit real-magnitude links, except when feeding a loss
 *    target in a pretaxLoss period (token to keep the region collapsed).
 *  - Intermediate nodes balance where possible. When operating loss creates
 *    an in/out imbalance at totalProfit, non-op income is split: POOL flows
 *    to totalProfit, remainder to an "Op. Loss Absorbed" sink.
 *
 * Shape returned by `buildIncomeSankeyModel`:
 *   {
 *     nodes: [{ id, name, kind, value, column }],
 *     links: [{ from, to, weight, flow }],
 *     hasLoss: boolean,
 *     notes: string[],
 *   }
 *
 * `kind`  in 'revenue' | 'profit' | 'cost' | 'income' | 'loss'
 * `flow`  in 'profit' | 'cost' | 'income' | 'loss' | 'token'
 *
 * Every node carries an explicit `column` (0-4). No unpinned nodes.
 * `finalize()` stamps `rescaled: true` on nodes whose visual height
 * misrepresents their label value (token-sized or flow-constrained).
 */

// --- Tuning constants ---
const DUST_FACTOR = 1e-9;
const TOKEN_THRESHOLD_FACTOR = 1e-7;
const RESCALE_LOW = 0.8;
const RESCALE_HIGH = 1.25;

/** Coerce to a finite number, or null (field absent). */
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * @typedef {Object} IncomeStatementRow
 * @property {number} revenue — Required. Model returns empty if absent or ≤ 0.
 * @property {number} [costOfRevenue] — Derived from revenue − grossProfit if absent.
 * @property {number} [grossProfit] — Derived from revenue − costOfRevenue if absent.
 * @property {number} [operatingExpenses] — Derived from grossProfit − operatingIncome if absent.
 * @property {number} [operatingIncome] — Derived from grossProfit − operatingExpenses if absent.
 * @property {number} [researchAndDevelopmentExpenses] — OpEx child.
 * @property {number} [sellingGeneralAndAdministrativeExpenses] — OpEx child.
 * @property {number} [generalAndAdministrativeExpenses] — Fallback for SGA (summed with selling).
 * @property {number} [sellingAndMarketingExpenses] — Fallback for SGA.
 * @property {number} [depreciationAndAmortization] — OpEx child (dropped if double-counted).
 * @property {number} [ebit] — Cross-check only (detects divergence from operatingIncome).
 * @property {number} [incomeBeforeTax] — Derived from netIncome + incomeTaxExpense if absent.
 * @property {number} [interestExpense] — Below-the-line distribution.
 * @property {number} [incomeTaxExpense] — Negative = tax benefit (income source).
 * @property {number} [netIncome] — Terminal node.
 */

/**
 * Builds the Sankey model for one period's income statement row.
 * @param {IncomeStatementRow} row
 */
export const buildIncomeSankeyModel = (row) => {
  const nodes = new Map();
  const links = [];
  const notes = [];
  let hasLoss = false;

  const setNode = (id, name, kind, value, column) => {
    nodes.set(id, { id, name, kind, value, column });
  };

  const addLink = (from, to, weight, flow) => {
    if (weight == null || !(weight > 0)) return;
    links.push({ from, to, weight, flow });
  };

  const empty = (note) => ({ nodes: [], links: [], hasLoss: false, notes: note ? [note] : [] });

  if (!row) return empty(null);

  const REV = num(row.revenue);
  if (REV == null || REV <= 0) return empty('Income statement unavailable for this period.');

  const DUST = Math.abs(REV) * DUST_FACTOR;

  // --- Field resolution ---
  let COGS = num(row.costOfRevenue);
  let GP = num(row.grossProfit);
  if (GP == null && COGS != null) GP = REV - COGS;
  if (COGS == null && GP != null) COGS = REV - GP;

  let OPEX = num(row.operatingExpenses);
  let EBIT = num(row.operatingIncome);
  if (GP != null) {
    if (OPEX == null && EBIT != null) OPEX = GP - EBIT;
    if (EBIT == null && OPEX != null) EBIT = GP - OPEX;
  }

  const RND = num(row.researchAndDevelopmentExpenses);
  let SGA = num(row.sellingGeneralAndAdministrativeExpenses);
  if (SGA == null) {
    const gAndA = num(row.generalAndAdministrativeExpenses);
    const sAndM = num(row.sellingAndMarketingExpenses);
    if (gAndA != null || sAndM != null) SGA = (gAndA || 0) + (sAndM || 0);
  }
  const DNA = num(row.depreciationAndAmortization);

  const INT = num(row.interestExpense);
  const TAX = num(row.incomeTaxExpense);
  const NI = num(row.netIncome);
  let PRETAX = num(row.incomeBeforeTax);
  if (PRETAX == null && NI != null) PRETAX = NI + (TAX || 0);
  const EBIT_RAW = num(row.ebit);

  // --- Derived values ---
  const INT_out = Math.max(0, INT != null ? INT : 0);
  const TAX_out = Math.max(0, TAX != null ? TAX : 0);

  // --- Stage A: Revenue split (col 0 -> col 1) ---
  if (GP == null) {
    return empty('Income statement detail unavailable for this period.');
  }

  setNode('revenue', 'Revenue', 'revenue', REV, 0);

  const grossLoss = GP < 0;
  if (grossLoss) {
    hasLoss = true;
    notes.push('Gross loss this period.');
    setNode('grossProfit', 'Gross Profit', 'loss', GP, 1);
    setNode('costOfRevenue', 'Cost of Revenue', 'cost', COGS, 1);
    // GP is a loss node: receives token inbound (DUST) to keep loss region collapsed.
    addLink('revenue', 'grossProfit', DUST, 'token');
    // Full revenue consumed by costs
    addLink('revenue', 'costOfRevenue', REV, 'cost');
  } else {
    setNode('grossProfit', 'Gross Profit', 'profit', GP, 1);
    setNode('costOfRevenue', 'Cost of Revenue', 'cost', COGS, 1);
    addLink('revenue', 'grossProfit', GP, 'profit');
    addLink('revenue', 'costOfRevenue', COGS, 'cost');
  }

  // --- Stage B: Gross-Profit split (col 1 -> col 2) ---
  if (EBIT == null) {
    notes.push('Operating breakdown unavailable for this period.');
    return finalize(nodes, links, notes, hasLoss);
  }

  const operatingLoss = EBIT < 0;
  if (operatingLoss) {
    hasLoss = true;
    notes.push('Operating loss this period.');
  }

  setNode('operatingIncome', 'Operating Income', operatingLoss ? 'loss' : 'profit', EBIT, 2);
  setNode('operatingExpenses', 'Operating Expenses', 'cost', OPEX, 2);

  if (grossLoss) {
    // GP is loss: Rule 1 (loss outbound = DUST) for both outbound links from GP
    addLink('grossProfit', 'operatingIncome', DUST, 'token');
    addLink('grossProfit', 'operatingExpenses', DUST, 'token');
  } else if (operatingLoss) {
    // GP > 0, EBIT < 0: token to loss node, full GP to OpEx
    addLink('grossProfit', 'operatingIncome', DUST, 'token');
    addLink('grossProfit', 'operatingExpenses', GP, 'cost');
  } else {
    // Fully profitable above the line. Cap OPEX at GP - EBIT to maintain balance
    // when FMP reports EBIT + OPEX > GP (data reconciliation differences).
    const opexOut = Math.min(OPEX, GP - EBIT);
    addLink('grossProfit', 'operatingIncome', EBIT, 'profit');
    addLink('grossProfit', 'operatingExpenses', opexOut, 'cost');
    const gpResidual = GP - EBIT - opexOut;
    if (gpResidual > DUST) {
      setNode('otherOperating', 'Other Operating', 'cost', gpResidual, 2);
      addLink('grossProfit', 'otherOperating', gpResidual, 'cost');
    }
  }

  // Helper: emit OpEx breakdown scaled to match actual inbound budget.
  // Inbound to OpEx: DUST (grossLoss), GP (operatingLoss), or min(OPEX, GP-EBIT) (profitable).
  const emitOpEx = () => {
    const budget = grossLoss ? DUST : operatingLoss ? GP : Math.min(OPEX, GP - EBIT);
    emitOpExBreakdown(OPEX, RND, SGA, DNA, DUST, notes, setNode, addLink, budget / OPEX);
  };

  // --- Stage C: Operating Income routing (col 2 -> col 3) ---
  if (PRETAX == null || NI == null) {
    notes.push('Below-the-line detail unavailable for this period.');
    emitOpEx();
    return finalize(nodes, links, notes, hasLoss);
  }

  const POOL = PRETAX + INT_out;
  const nonOp = POOL - EBIT;

  const pretaxLoss = PRETAX < 0;
  const tpCollapsed = pretaxLoss && (POOL <= 0 || operatingLoss);
  setNode('totalProfit', 'Total Profit', tpCollapsed ? 'loss' : 'profit', POOL, 3);
  if (pretaxLoss) {
    hasLoss = true;
    notes.push('Pre-tax loss this period.');
  }

  routeNonOp(operatingLoss, pretaxLoss, nonOp, EBIT, POOL, DUST, setNode, addLink);

  // --- Stage D: Total Profit distribution (col 3 -> col 4) ---
  const niLoss = NI < 0;
  if (niLoss) {
    hasLoss = true;
    notes.push('Net loss this period.');
  }

  setNode('netIncome', 'Net Income', niLoss ? 'loss' : 'income', NI, 4);

  const taxBenefit = TAX != null && TAX < 0;

  if (pretaxLoss && (POOL <= 0 || operatingLoss)) {
    // Truly no distributable flow: all outbound are DUST
    addLink('totalProfit', 'netIncome', DUST, 'token');
    if (INT_out > 0) {
      setNode('interest', 'Interest', 'cost', INT, 4);
      addLink('totalProfit', 'interest', DUST, 'token');
    }
    if (!taxBenefit && TAX_out > 0) {
      setNode('taxes', 'Taxes', 'cost', TAX, 4);
      addLink('totalProfit', 'taxes', DUST, 'token');
    }
    if (taxBenefit) {
      setNode('taxBenefit', 'Tax Benefit', 'income', Math.abs(TAX), 3);
      addLink('taxBenefit', 'netIncome', Math.abs(TAX), 'income');
    }
  } else {
    // Total Profit is profit: distribute POOL to children at real weights
    if (taxBenefit) {
      // No taxes outflow; route available budget to interest then NI, tax benefit separately
      setNode('taxBenefit', 'Tax Benefit', 'income', Math.abs(TAX), 3);
      addLink('taxBenefit', 'netIncome', Math.abs(TAX), 'income');

      const intCapped = Math.min(INT_out, POOL);
      if (intCapped > DUST) {
        setNode('interest', 'Interest', 'cost', INT, 4);
        addLink('totalProfit', 'interest', intCapped, 'cost');
      }
      const tpToNi = Math.max(DUST, POOL - intCapped);
      addLink('totalProfit', 'netIncome', tpToNi, niLoss ? 'loss' : 'income');
    } else {
      // Normal distribution: budget POOL split to INT → TAX → NI → residual.
      // Each allocation is capped so total never exceeds POOL.
      const intAlloc = Math.min(INT_out, POOL);
      const taxAlloc = Math.min(TAX_out, POOL - intAlloc);
      if (intAlloc > DUST) {
        setNode('interest', 'Interest', 'cost', INT, 4);
        addLink('totalProfit', 'interest', intAlloc, 'cost');
      }
      if (taxAlloc > DUST) {
        setNode('taxes', 'Taxes', 'cost', TAX, 4);
        addLink('totalProfit', 'taxes', taxAlloc, 'cost');
      }
      const niRemaining = Math.max(DUST, POOL - intAlloc - taxAlloc);
      const niReported = Math.max(0, NI);
      const niWeight = niReported > 0 ? Math.min(niReported, niRemaining) : niRemaining;
      const tpResidual = niRemaining - niWeight;
      if (tpResidual > DUST) {
        setNode('otherBelowLine', 'Other', 'cost', tpResidual, 4);
        addLink('totalProfit', 'otherBelowLine', tpResidual, 'cost');
      }
      addLink('totalProfit', 'netIncome', niWeight, niLoss ? 'loss' : 'income');
    }
  }

  // --- Stage E: OpEx breakdown (col 2 -> col 3) ---
  emitOpEx();

  // --- Data-consistency checks ---
  if (NI != null && PRETAX != null && TAX != null) {
    const gap = Math.abs(NI - (PRETAX - TAX));
    if (gap > Math.abs(REV) * 0.05) {
      notes.push('Data-provider figures are internally inconsistent (NI ≠ Pre-Tax − Tax); some ribbons are budget-capped (↕).');
    }
  }
  if (EBIT_RAW != null && EBIT != null && Math.abs(EBIT_RAW - EBIT) > Math.abs(REV) * 0.01) {
    notes.push('Data-provider reports divergent operating income vs. EBIT; non-operating flow may be overstated.');
  }

  return finalize(nodes, links, notes, hasLoss);
};

/**
 * Emit OpEx children (R&D, SG&A, D&A, Other OpEx) from operatingExpenses (col 2) to col 3.
 * @param {number} scaleFactor — multiplier for link weights (default 1). When operating loss
 *   causes inbound < OPEX, pass inbound/OPEX so outbound links sum to the inbound budget.
 *   Node values remain at real magnitudes for label display.
 */
function emitOpExBreakdown(OPEX, RND, SGA, DNA, DUST, notes, setNode, addLink, scaleFactor = 1) {
  if (OPEX == null || !(OPEX > 0)) return;

  let cand = [];
  if (RND != null && RND > 0) cand.push(['rnd', 'R&D', RND]);
  if (SGA != null && SGA > 0) cand.push(['sga', 'SG&A', SGA]);
  const hasDna = DNA != null && DNA > 0;
  if (hasDna) cand.push(['dna', 'D&A', DNA]);

  let known = cand.reduce((s, [, , v]) => s + v, 0);
  if (known > OPEX && hasDna) {
    // D&A double-counted (already inside COGS/SG&A) — drop it.
    cand = cand.filter(([id]) => id !== 'dna');
    known = cand.reduce((s, [, , v]) => s + v, 0);
  }
  if (known > OPEX) {
    notes.push('Operating-expense breakdown unavailable for this period.');
    return;
  }
  if (cand.length === 0) return;

  cand.forEach(([id, name, v]) => {
    setNode(id, name, 'cost', v, 3);              // node shows real value
    addLink('operatingExpenses', id, v * scaleFactor, 'cost');  // link scaled to budget
  });

  const residual = OPEX - known;
  if (residual > DUST) {
    setNode('otherOpex', 'Other OpEx', 'cost', residual, 3);
    addLink('operatingExpenses', 'otherOpex', residual * scaleFactor, 'cost');
  }
}

/**
 * Route operatingIncome → totalProfit and any non-op income/expense nodes.
 * Handles the 6-path matrix: nonOp sign × (operatingLoss, pretaxLoss, POOL sign).
 */
function routeNonOp(operatingLoss, pretaxLoss, nonOp, EBIT, POOL, DUST, setNode, addLink) {
  if (nonOp > DUST) {
    // Non-op INCOME: source at col 2 feeding totalProfit
    setNode('nonOpIncome', 'Non-Op Income', 'income', nonOp, 2);
    if (operatingLoss) {
      addLink('operatingIncome', 'totalProfit', DUST, 'token');
      if (!pretaxLoss) {
        // Cap inflow at POOL so totalProfit balances. Remainder → lossAbsorbed.
        addLink('nonOpIncome', 'totalProfit', POOL, 'income');
        const absorbed = nonOp - POOL;
        setNode('lossAbsorbed', 'Op. Loss Absorbed', 'cost', absorbed, 3);
        addLink('nonOpIncome', 'lossAbsorbed', absorbed, 'cost');
        // DFS anchor: ensures lossAbsorbed shares totalProfit's level
        addLink('operatingIncome', 'lossAbsorbed', DUST, 'token');
      } else {
        // pretaxLoss: entire loss region stays token-sized
        addLink('nonOpIncome', 'totalProfit', DUST, 'token');
      }
    } else {
      addLink('operatingIncome', 'totalProfit', EBIT, 'profit');
      addLink('nonOpIncome', 'totalProfit', nonOp, 'income');
    }
    return;
  }

  if (nonOp < -DUST) {
    // Non-op EXPENSE: sink at col 3
    setNode('nonOpExpense', 'Non-Op Expense', 'cost', Math.abs(nonOp), 3);
    if (operatingLoss) {
      addLink('operatingIncome', 'totalProfit', DUST, 'token');
      addLink('operatingIncome', 'nonOpExpense', DUST, 'token');
    } else if (POOL > 0) {
      addLink('operatingIncome', 'totalProfit', POOL, 'profit');
      addLink('operatingIncome', 'nonOpExpense', EBIT - POOL, 'cost');
    } else {
      addLink('operatingIncome', 'nonOpExpense', EBIT, 'cost');
      addLink('operatingIncome', 'totalProfit', DUST, 'token');
    }
    return;
  }

  // nonOp ≈ 0: no non-op node needed
  if (operatingLoss) {
    addLink('operatingIncome', 'totalProfit', DUST, 'token');
  } else {
    addLink('operatingIncome', 'totalProfit', EBIT, 'profit');
  }
}

/**
 * Sort links to prevent ribbon crossings. Highcharts v11.1.0 uses data-array
 * order for BOTH source-side exit and target-side entry positions. If links
 * sharing a target arrive in an order that doesn't match their source nodes'
 * vertical positions, ribbons cross (X-pattern). Sorting by source-node kind
 * priority ensures green (income/profit) ribbons stack at the top, purple (loss)
 * in the middle, red (cost) at the bottom — matching node vertical ordering.
 */
function sortLinksForLayout(links, nodes) {
  const kindPriority = { revenue: 0, income: 0, profit: 0, loss: 1, cost: 2 };
  const kindOf = {};
  nodes.forEach((n) => { kindOf[n.id] = n.kind; });
  const hasInbound = new Set();
  links.forEach((l) => hasInbound.add(l.to));
  links.sort((a, b) => {
    const aPri = kindPriority[kindOf[a.from]] ?? 1;
    const bPri = kindPriority[kindOf[b.from]] ?? 1;
    if (aPri !== bPri) return aPri - bPri;
    const aTo = kindPriority[kindOf[a.to]] ?? 1;
    const bTo = kindPriority[kindOf[b.to]] ?? 1;
    if (aTo !== bTo) return aTo - bTo;
    if (a.to === b.to) {
      const aOrphan = hasInbound.has(a.from) ? 1 : 0;
      const bOrphan = hasInbound.has(b.from) ? 1 : 0;
      if (aOrphan !== bOrphan) return aOrphan - bOrphan;
    }
    return b.weight - a.weight;
  });
}

/**
 * Post-processing: mark any node whose visual height significantly
 * misrepresents its label value with `rescaled: true` (renders ↕ in label).
 * Three cases:
 *  1. Token-sized: visual weight < REV × 1e-7 (node collapsed to DUST)
 *  2. Under-supplied: visual weight < 80% of label value (node receives
 *     less flow than its accounting magnitude due to routing constraints)
 *  3. Over-supplied: visual weight > 125% of label value (node receives
 *     more flow than its accounting magnitude due to budget redistribution)
 */
function markRescaledNodes(nodes, links) {
  const inSum = new Map();
  const outSum = new Map();
  links.forEach((l) => {
    outSum.set(l.from, (outSum.get(l.from) || 0) + l.weight);
    inSum.set(l.to, (inSum.get(l.to) || 0) + l.weight);
  });
  const rev = nodes.get('revenue');
  const tokenThreshold = rev ? Math.abs(rev.value) * TOKEN_THRESHOLD_FACTOR : 1;
  for (const [, node] of nodes) {
    const visual = Math.max(inSum.get(node.id) || 0, outSum.get(node.id) || 0);
    if (visual < tokenThreshold) {
      node.rescaled = true;
    } else if (node.value != null && Math.abs(node.value) > 0) {
      const ratio = visual / Math.abs(node.value);
      if (ratio < RESCALE_LOW || ratio > RESCALE_HIGH) node.rescaled = true;
    }
  }
}

/**
 * Prune nodes not referenced by any link. Sort links for vertical layout.
 * Stamp pct on each link. Mark token-sized nodes as rescaled.
 */
function finalize(nodes, links, notes, hasLoss) {
  const referenced = new Set();
  links.forEach((l) => { referenced.add(l.from); referenced.add(l.to); });
  markRescaledNodes(nodes, links);
  const allNodes = [...nodes.values()].filter((n) => referenced.has(n.id));
  sortLinksForLayout(links, nodes);
  const absRev = allNodes.find((n) => n.id === 'revenue')?.value || 1;
  return {
    nodes: allNodes,
    links: links.map((l) => ({ ...l, pct: (l.weight / Math.abs(absRev)) * 100 })),
    hasLoss,
    notes,
  };
}

export default buildIncomeSankeyModel;
